import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import apiService, {
  parseGetRefundsResponse,
  type GetRefundsRequest,
} from "../../services/api";
import useHubs from "../../hooks/useHubs";
import Pagination from "../../components/ui/Pagination";
import SortableTable, {
  SortableColumn,
  SortOrder,
} from "../../components/common/SortableTable";
import { usePagination } from "../../hooks/usePagination";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import CustomLoading from "../../components/common/CustomLoading";
import { formatMobileNumber } from "../../utils/formatters";
import {
  formatRefundRideEndDate,
  formatRefundRideStartDate,
} from "./refundDateFormat";
import { REFUND_STATUS_CODE } from "./editRefundDepositBreakdown";
import {
  DELETE_REASON_CATEGORY,
  findDeleteReasonOtherCodeValue,
  mapDeleteReasonSelectOptions,
  resolveRefundDeleteReasonPayload,
  type CodeCommonOption,
} from "./refundDeleteReasonUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAuth";
import { getHubId } from "../../utils/cookieUtils";
import {
  emptyRefundSearchFields,
  loadRefundListFilters,
  saveRefundListFilters,
  type RefundSearchFields,
} from "./refundListFiltersStorage";
import DeleteIcon from "../../assets/icons/DeleteIcon.svg";
import useRBAC, { ROLES } from "../../hooks/useRBAC";
import DeleteConfirmDialog from "../../components/common/DeleteConfirmDialog";
import { useNotification } from "../../contexts/NotificationContext";
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import CsvExport from "../../components/common/CsvExport";
import { API_ENDPOINTS } from "../../constants";
import { useHeaderToolbarAfterExport } from "../../contexts/HeaderToolbarContext";
import {
  buildRefundsExportFilename,
  fetchRefundsForExport,
  transformRefundsForCsvExport,
} from "./refundExportUtils";
import {
  filterRowsByConflictBookingIds,
  getConflictBookingMasterIds,
  getRefundSourceLabel,
  groupConflictRefundsByBooking,
  isRefundAddedByWeb,
} from "./refundConflictUtils";
import RefundConflictBookingTable from "./RefundConflictBookingTable";
import type { RefundRow } from "./refundTypes";

dayjs.extend(utc);

const REFUND_ACTION_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.HUB_MANAGER,
  ROLES.HUB_CLUSTER_MANAGER,
] as const;

/** Roles allowed to export refund CSV (Admin, Cluster Manager, Hub Manager, Finance). */
const REFUND_EXPORT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.HUB_MANAGER,
  ROLES.HUB_CLUSTER_MANAGER,
  ROLES.FINANCE,
] as const;

const REFUND_STATUS_CHIP_STYLES: Record<
  number,
  { backgroundColor: string; color: string }
> = {
  [REFUND_STATUS_CODE.ADDED]: {
    backgroundColor: "rgba(255, 248, 220, 1)",
    color: "rgba(184, 134, 11, 1)",
  },
  [REFUND_STATUS_CODE.CREATED]: {
    backgroundColor: "rgba(227, 242, 253, 1)",
    color: "rgba(25, 118, 210, 1)",
  },
  [REFUND_STATUS_CODE.PROCESSED]: {
    backgroundColor: "rgba(212, 255, 199, 1)",
    color: "rgba(31, 113, 13, 1)",
  },
  [REFUND_STATUS_CODE.FAILED]: {
    backgroundColor: "rgba(255, 227, 227, 1)",
    color: "rgba(197, 0, 0, 1)",
  },
};

function getRefundStatusChipStyles(code: string) {
  const n = Number(code);
  return (
    REFUND_STATUS_CHIP_STYLES[n] ?? {
      backgroundColor: "rgba(245, 245, 245, 1)",
      color: "rgba(117, 117, 117, 1)",
    }
  );
}

const REFUND_STATUS_CREATED_PENDING_TOOLTIP =
  "Refund is created. It may take a few minutes to proceed.";

function RefundStatusChip({ label, code }: { label: string; code: string }) {
  const styles = getRefundStatusChipStyles(code);
  const statusCode = Number(code);
  const showCreatedPendingInfo =
    Number.isFinite(statusCode) && statusCode === REFUND_STATUS_CODE.CREATED;

  const chip = (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600,
        backgroundColor: styles.backgroundColor,
        color: styles.color,
      }}
    />
  );

  if (!showCreatedPendingInfo) return chip;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {chip}
      <Tooltip
        title={REFUND_STATUS_CREATED_PENDING_TOOLTIP}
        arrow
        placement="top"
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "help",
          }}
          aria-label={REFUND_STATUS_CREATED_PENDING_TOOLTIP}
        >
          <CircularProgress
            size={16}
            thickness={5}
            sx={{ color: "rgba(25, 118, 210, 1)" }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
}

const REFUND_SEARCH_DEBOUNCE_MS = 400;

const parsePersistedRideDay = (
  iso: string | null | undefined,
): Dayjs | null => {
  if (!iso) return null;
  const d = dayjs(iso, "YYYY-MM-DD", true);
  return d.isValid() ? d.startOf("day") : null;
};

const toRideFilterStartIso = (d: Dayjs) =>
  d.startOf("day").utcOffset(0, true).toISOString();

const toRideFilterEndIso = (d: Dayjs) =>
  d.endOf("day").utcOffset(0, true).toISOString();

type RefundListTab = "all" | "conflicts";

const CONFLICT_REFUNDS_FETCH_PAGE_SIZE = 10000;

const getScalar = (item: Record<string, unknown>, keys: string[]): unknown => {
  for (const k of keys) {
    if (k in item && item[k] != null && item[k] !== "") return item[k];
  }
  return undefined;
};

const formatDurationValue = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
};

const compareRefundSortValues = (
  aValue: string | number | boolean | null | undefined,
  bValue: string | number | boolean | null | undefined,
  order: Exclude<SortOrder, null>,
): number => {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return order === "asc" ? -1 : 1;
  if (bValue == null) return order === "asc" ? 1 : -1;

  if (typeof aValue === "number" && typeof bValue === "number") {
    return order === "asc" ? aValue - bValue : bValue - aValue;
  }

  if (typeof aValue === "boolean" && typeof bValue === "boolean") {
    const av = aValue ? 1 : 0;
    const bv = bValue ? 1 : 0;
    return order === "asc" ? av - bv : bv - av;
  }

  const aText = String(aValue).toLowerCase();
  const bText = String(bValue).toLowerCase();
  if (aText < bText) return order === "asc" ? -1 : 1;
  if (aText > bText) return order === "asc" ? 1 : -1;
  return 0;
};

const mapItemToRow = (item: unknown, idx: number): RefundRow => {
  const r =
    item && typeof item === "object" ? (item as Record<string, unknown>) : {};

  const get = (key: string) => getScalar(r, [key]);

  const phone = get("phonenumber");

  return {
    refundId: String(get("refundid") ?? idx),
    bookingMasterId: String(get("bookingmasterid") ?? ""),
    name: String(get("name") ?? ""),
    phoneNumber:
      typeof phone === "string" || typeof phone === "number"
        ? formatMobileNumber(phone) || String(phone)
        : "",
    rideCompleteDate: formatRefundRideEndDate(r),
    bookingAmount: Number(get("bookingamount") ?? 0),
    /** API `proposedamount` = refundable amount; `refundableamount` = proposed refund. */
    refundableAmount: Number(
      getScalar(r, ["proposedamount", "proposedAmount"]) ?? 0,
    ),
    proposedRefundAmount: Number(get("refundableamount") ?? 0),
    razorpayRefundId: String(get("razorpayrefundid") ?? ""),
    refundInitiatedOn: String(get("refundinitiatedon") ?? ""),
    /** List API exposes `paymentstatus` (see `RefundApiRow`); optional `refundstatus` if added later. */
    refundStatus: String(
      getScalar(r, [
        "refundstatus",
        "refundStatus",
        "RefundStatus",
        "paymentstatus",
        "paymentStatus",
        "PaymentStatus",
      ]) ?? "",
    ),
    paymentMethod: Number(get("paymentmethod") ?? 0),
    paymentStatus: (() => {
      const v = get("paymentstatus") ?? get("paymentStatus");
      const n = Number(v);
      return Number.isFinite(n) ? n : -1;
    })(),
    rawItem: r as Record<string, unknown>,
    hub: String(get("hub") ?? ""),
    hubName: String(get("hubname") ?? ""),
    transactionId: String(get("transactionid") ?? ""),
    vehicle:
      String(
        getScalar(r, [
          "vehiclenum",
          "vehicleNum",
          "VehicleNum",
          "vehiclename",
          "vehicleName",
          "VehicleName",
        ]) ?? "",
      ).trim() || null,
    rideStartDate: formatRefundRideStartDate(r) || null,
    duration:
      formatDurationValue(
        getScalar(r, [
          "duration",
          "Duration",
          "tripduration",
          "tripDuration",
          "rentedduration",
          "rentedDuration",
        ]),
      ) || null,
    isAddedByWeb: isRefundAddedByWeb(r),
  };
};

function buildRefundSearchApiParams(filters: RefundSearchFields): {
  bookingmasterid: number | null;
  username: string;
  mobilenumber: string;
  vehiclenum: string;
} {
  const bookingIdTrim = filters.bookingId.trim();
  const userName = filters.userName.trim();
  const mobileDigits = filters.mobileNumber.replace(/\D/g, "");
  const vehicleNum = filters.vehicleNum.trim();

  let bookingmasterid: number | null = null;
  if (bookingIdTrim) {
    const n = Number.parseInt(bookingIdTrim.replace(/\D/g, ""), 10);
    if (Number.isFinite(n) && n > 0) bookingmasterid = n;
  }

  return {
    bookingmasterid,
    username: userName,
    mobilenumber: mobileDigits,
    vehiclenum: vehicleNum,
  };
}

function toRefundStatusFilterCode(refundStatus: string): number | null {
  const rt = String(refundStatus).trim();
  if (rt === "" || rt === "0") return null;
  const n = Number(rt);
  return Number.isFinite(n) ? n : null;
}

const RefundManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  const { hasAnyRole } = useRBAC();
  const userCanExportRefunds = hasAnyRole([...REFUND_EXPORT_ROLES]);
  const { setAfterExportSlot } = useHeaderToolbarAfterExport();
  const { showNotification } = useNotification();
  const canEditOrDeleteRefund = hasAnyRole([...REFUND_ACTION_ROLES]);
  const initialFilters = useMemo(() => loadRefundListFilters(), []);

  const [hub, setHub] = useState(() => initialFilters?.hub ?? "");
  const [refundStatus, setRefundStatus] = useState(() => {
    const r = initialFilters?.paymentStatus ?? "";
    return r === "0" ? "" : r;
  });
  const [refundStatusOptions, setRefundStatusOptions] = useState<
    { codevalue?: string | number; codedisplayname?: string }[]
  >([]);
  const [searchFilters, setSearchFilters] = useState<RefundSearchFields>(() =>
    initialFilters?.searchFields
      ? { ...initialFilters.searchFields }
      : emptyRefundSearchFields(),
  );
  const [debouncedSearchFilters, setDebouncedSearchFilters] =
    useState<RefundSearchFields>(searchFilters);
  const paginationPageRef = useRef(0);
  const [rideDateRange, setRideDateRange] = useState<
    [Dayjs | null, Dayjs | null]
  >(() => {
    const [from, to] = initialFilters?.rideDateRange ?? [null, null];
    const start = parsePersistedRideDay(from);
    const end = parsePersistedRideDay(to);
    if (start || end) return [start, end];
    const legacy = parsePersistedRideDay(initialFilters?.rideCompletionDate);
    return legacy ? [legacy, legacy] : [null, null];
  });
  const [paymentMethodLabels, setPaymentMethodLabels] = useState<
    Record<string, string>
  >({});
  const [listTab, setListTab] = useState<RefundListTab>("all");
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [conflictSourceRows, setConflictSourceRows] = useState<RefundRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingBookingMasterId, setPendingBookingMasterId] = useState<
    string | null
  >(null);
  const [initiating, setInitiating] = useState(false);
  const [refundPendingDelete, setRefundPendingDelete] =
    useState<RefundRow | null>(null);
  const [deleteRefundReasonOptions, setDeleteRefundReasonOptions] = useState<
    CodeCommonOption[]
  >([]);
  const [deleteRefundReasonCode, setDeleteRefundReasonCode] = useState("");
  const [deleteRefundRemark, setDeleteRefundRemark] = useState("");
  const [deleteRefundLoading, setDeleteRefundLoading] = useState(false);
  const { hubOptions, loading: hubsLoading } = useHubs(setError);

  /** Hub-scoped roles: header hub dropdown (`getHubId`). Super admin: page hub filter or all hubs (0). */
  const resolveHubIdForApi = useCallback((): number => {
    if (!isSuperAdmin) {
      const hid = getHubId();
      return hid != null && Number.isFinite(Number(hid)) ? Number(hid) : 0;
    }
    if (hub) {
      const found = hubOptions.find((h) => h.hubname === hub);
      return found ? Number(found.hubmasterid) : 0;
    }
    return 0;
  }, [isSuperAdmin, hub, hubOptions]);

  const conflictBookingIds = useMemo(
    () => getConflictBookingMasterIds(conflictSourceRows),
    [conflictSourceRows],
  );

  const allConflictRows = useMemo(
    () =>
      filterRowsByConflictBookingIds(conflictSourceRows, conflictBookingIds),
    [conflictSourceRows, conflictBookingIds],
  );

  const conflictBookingGroups = useMemo(
    () => groupConflictRefundsByBooking(allConflictRows),
    [allConflictRows],
  );

  const [expandedConflictBookingIds, setExpandedConflictBookingIds] = useState<
    Set<string>
  >(() => new Set());
  const [conflictSortBy, setConflictSortBy] = useState<keyof RefundRow | null>(
    null,
  );
  const [conflictSortOrder, setConflictSortOrder] = useState<SortOrder>(null);

  const getConflictSortValue = useCallback(
    (
      row: RefundRow,
      key: keyof RefundRow,
    ): string | number | boolean | null | undefined => {
      switch (key) {
        case "rideStartDate":
          return formatRefundRideStartDate(row.rawItem) || row.rideStartDate;
        case "rideCompleteDate":
          return formatRefundRideEndDate(row.rawItem) || row.rideCompleteDate;
        case "vehicle":
          return row.vehicle || "";
        case "duration":
          return row.duration || "";
        default:
          {
            const value = row[key];
            if (
              typeof value === "string" ||
              typeof value === "number" ||
              typeof value === "boolean" ||
              value == null
            ) {
              return value;
            }
            return String(value);
          }
      }
    },
    [],
  );

  const sortedConflictBookingGroups = useMemo(() => {
    if (!conflictSortBy || !conflictSortOrder) return conflictBookingGroups;

    const sortKey = conflictSortBy;
    const sortOrder = conflictSortOrder;
    const compareRows = (a: RefundRow, b: RefundRow) =>
      compareRefundSortValues(
        getConflictSortValue(a, sortKey),
        getConflictSortValue(b, sortKey),
        sortOrder,
      );

    return conflictBookingGroups
      .map((group) => ({
        ...group,
        refunds: [...group.refunds].sort(compareRows),
      }))
      .sort((a, b) => {
        const firstA = a.refunds[0];
        const firstB = b.refunds[0];
        if (!firstA && !firstB) return 0;
        if (!firstA) return 1;
        if (!firstB) return -1;
        return compareRows(firstA, firstB);
      });
  }, [conflictBookingGroups, conflictSortBy, conflictSortOrder, getConflictSortValue]);

  const paginationTotalItems =
    listTab === "all" ? totalCount : sortedConflictBookingGroups.length;

  const pagination = usePagination({
    totalItems: paginationTotalItems,
    initialPage: initialFilters?.page ?? 0,
    initialRowsPerPage: initialFilters?.rowsPerPage ?? 10,
  });
  paginationPageRef.current = pagination.page;

  const displayConflictGroups = useMemo(() => {
    const start = pagination.page * pagination.rowsPerPage;
    return sortedConflictBookingGroups.slice(
      start,
      start + pagination.rowsPerPage,
    );
  }, [sortedConflictBookingGroups, pagination.page, pagination.rowsPerPage]);

  const tableLoading = listTab === "all" ? loading : conflictLoading;

  const buildGetRefundsFilterBody = useCallback(
    (pageNumber: number, pageSize: number): GetRefundsRequest => {
      const { bookingmasterid, username, mobilenumber, vehiclenum } =
        buildRefundSearchApiParams(debouncedSearchFilters);

      const [rideFrom, rideTo] = rideDateRange;
      const sameDay =
        rideFrom &&
        rideTo &&
        rideFrom.format("YYYY-MM-DD") === rideTo.format("YYYY-MM-DD");

      let ridestartdate = null;
      let ridecompletiondate = null;
      if (sameDay && rideFrom) {
        const dayIso = toRideFilterStartIso(rideFrom);
        ridestartdate = dayIso;
        ridecompletiondate = toRideFilterEndIso(rideFrom);
      } else {
        if (rideFrom) ridestartdate = toRideFilterStartIso(rideFrom);
        if (rideTo) ridecompletiondate = toRideFilterEndIso(rideTo);
        else if (rideFrom) ridecompletiondate = toRideFilterEndIso(rideFrom);
      }

      return {
        hubid: resolveHubIdForApi(),
        paymentstatus: toRefundStatusFilterCode(refundStatus),
        bookingmasterid,
        username,
        mobilenumber,
        vehiclenum,
        ridestartdate,
        ridecompletiondate,
        pageNumber,
        pageSize,
      };
    },
    [resolveHubIdForApi, refundStatus, debouncedSearchFilters, rideDateRange],
  );

  const fetchRefunds = useCallback(
    async (pageOverride?: number, rowsOverride?: number) => {
      try {
        setLoading(true);
        setError(null);
        const effectivePage =
          pageOverride !== undefined ? pageOverride : pagination.page;
        const effectiveRows =
          rowsOverride !== undefined ? rowsOverride : pagination.rowsPerPage;
        const pageSize = Math.max(1, Number(effectiveRows) || 10);
        const pageNumber = effectivePage + 1;

        const body = buildGetRefundsFilterBody(pageNumber, pageSize);

        const response = await apiService.getRefunds(body);
        const { items, total } = parseGetRefundsResponse(response);
        const mapped = items.map((it, i) => mapItemToRow(it, i));
        setTotalCount(total);
        setRows(mapped);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load refunds";
        setError(message);
        setTotalCount(0);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [buildGetRefundsFilterBody, pagination.page, pagination.rowsPerPage],
  );

  const fetchConflictRefunds = useCallback(async () => {
    try {
      setConflictLoading(true);
      setError(null);
      const body = buildGetRefundsFilterBody(
        1,
        CONFLICT_REFUNDS_FETCH_PAGE_SIZE,
      );
      const response = await apiService.getRefunds(body);
      const { items } = parseGetRefundsResponse(response);
      setConflictSourceRows(items.map((it, i) => mapItemToRow(it, i)));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load conflict refunds";
      setError(message);
      setConflictSourceRows([]);
    } finally {
      setConflictLoading(false);
    }
  }, [buildGetRefundsFilterBody]);

  const runRefundsExport = useCallback(async () => {
    const body = buildGetRefundsFilterBody(1, 10000);
    return fetchRefundsForExport(body, rideDateRange[0], rideDateRange[1]);
  }, [buildGetRefundsFilterBody, rideDateRange]);

  const exportFilename = useMemo(
    () => buildRefundsExportFilename(rideDateRange[0], rideDateRange[1]),
    [rideDateRange],
  );

  useEffect(() => {
    if (!userCanExportRefunds) {
      setAfterExportSlot(null);
      return;
    }
    setAfterExportSlot(
      <CsvExport
        apiEndpoint={API_ENDPOINTS.BOOKINGS.GET_REFUNDS}
        method="post"
        filename={exportFilename}
        buttonLabel="Export CSV"
        disabled={loading}
        customApiCall={() => runRefundsExport()}
        dataTransformer={(data) =>
          transformRefundsForCsvExport(
            data,
            refundStatusOptions,
            paymentMethodLabels,
          )
        }
      />,
    );
    return () => setAfterExportSlot(null);
  }, [
    userCanExportRefunds,
    exportFilename,
    loading,
    paymentMethodLabels,
    refundStatusOptions,
    runRefundsExport,
    setAfterExportSlot,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchFilters((prev) => {
        const searchChanged =
          prev.bookingId !== searchFilters.bookingId ||
          prev.userName !== searchFilters.userName ||
          prev.mobileNumber !== searchFilters.mobileNumber ||
          prev.vehicleNum !== searchFilters.vehicleNum;
        if (searchChanged && paginationPageRef.current !== 0) {
          pagination.goToPage(0);
        }
        return searchFilters;
      });
    }, REFUND_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // goToPage from usePagination is not referentially stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  const hubNameOptions = useMemo(
    () => (hubsLoading ? [] : hubOptions.map((h) => h.hubname)),
    [hubsLoading, hubOptions],
  );

  useEffect(() => {
    const state = location.state as
      | { refresh?: boolean; refreshHubs?: boolean }
      | undefined;
    if (state?.refresh || state?.refreshHubs) {
      if (listTab === "all") void fetchRefunds();
      else void fetchConflictRefunds();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    location.state,
    navigate,
    location.pathname,
    fetchRefunds,
    fetchConflictRefunds,
    listTab,
  ]);

  useEffect(() => {
    if (listTab !== "all") return;
    void fetchRefunds();
  }, [fetchRefunds, listTab]);

  useEffect(() => {
    if (listTab !== "conflicts") return;
    void fetchConflictRefunds();
  }, [fetchConflictRefunds, listTab]);

  useEffect(() => {
    saveRefundListFilters({
      hub,
      paymentStatus: refundStatus,
      searchFields: searchFilters,
      rideDateRange: [
        rideDateRange[0]?.format("YYYY-MM-DD") ?? null,
        rideDateRange[1]?.format("YYYY-MM-DD") ?? null,
      ],
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
    });
  }, [
    hub,
    refundStatus,
    searchFilters,
    rideDateRange,
    pagination.page,
    pagination.rowsPerPage,
  ]);

  /** Keep current page valid if result count shrinks (e.g. after restore). */
  const handleListTabChange = (
    _: React.SyntheticEvent,
    value: RefundListTab,
  ) => {
    setListTab(value);
    setExpandedConflictBookingIds(new Set());
    pagination.goToPage(0);
  };

  const toggleConflictBookingExpand = useCallback((bookingMasterId: string) => {
    setExpandedConflictBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(bookingMasterId)) next.delete(bookingMasterId);
      else next.add(bookingMasterId);
      return next;
    });
  }, []);

  const handleConflictRequestSort = useCallback((columnId: keyof RefundRow) => {
    if (conflictSortBy === columnId) {
      if (conflictSortOrder === "asc") {
        setConflictSortOrder("desc");
        return;
      }
      if (conflictSortOrder === "desc") {
        setConflictSortBy(null);
        setConflictSortOrder(null);
        return;
      }
    }
    setConflictSortBy(columnId);
    setConflictSortOrder("asc");
  }, [conflictSortBy, conflictSortOrder]);

  useEffect(() => {
    if (tableLoading) return;
    const tp = pagination.totalPages;
    if (tp <= 0) {
      if (pagination.page !== 0) pagination.goToPage(0);
      return;
    }
    if (pagination.page >= tp) {
      pagination.goToPage(tp - 1);
    }
    // goToPage from usePagination is not referentially stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pagination.totalPages, pagination.page]);

  useEffect(() => {
    const fetchRefundStatus = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("RefundStatus");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        setRefundStatusOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load refund status", err);
      }
    };
    void fetchRefundStatus();
  }, []);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const res: unknown =
          await apiService.getAllCodeCommonByCategory("PaymentMethod");
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        const list = Array.isArray(data) ? data : [];
        const labels: Record<string, string> = {};
        for (const opt of list as {
          codevalue?: string | number;
          codedisplayname?: string;
        }[]) {
          const key = String(opt.codevalue ?? "");
          if (key) labels[key] = String(opt.codedisplayname ?? key);
        }
        setPaymentMethodLabels(labels);
      } catch (err) {
        console.error("Failed to load payment methods for export", err);
      }
    };
    void fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const fetchDeleteReasons = async () => {
      try {
        const res: unknown = await apiService.getAllCodeCommonByCategory(
          DELETE_REASON_CATEGORY,
        );
        const data = Array.isArray(res)
          ? res
          : (res as { data?: unknown })?.data;
        setDeleteRefundReasonOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load delete reasons", err);
      }
    };
    void fetchDeleteReasons();
  }, []);

  const deleteRefundReasonSelectOptions = useMemo(
    () => mapDeleteReasonSelectOptions(deleteRefundReasonOptions),
    [deleteRefundReasonOptions],
  );

  const deleteRefundReasonOtherCode = useMemo(
    () => findDeleteReasonOtherCodeValue(deleteRefundReasonOptions),
    [deleteRefundReasonOptions],
  );

  const refundStatusSelectOptions = useMemo(() => {
    const rest = refundStatusOptions.map((opt) => ({
      label: String(opt.codedisplayname ?? ""),
      value: String(opt.codevalue ?? ""),
    }));
    return [...rest];
  }, [refundStatusOptions]);

  const openEditDialog = (row: RefundRow) => {
    // let data = [
    //   {
    //     refundid: 1,
    //     bookingmasterid: 272,
    //     name: "gautam viradiya",
    //     phonenumber: "12364",
    //     refundableamount: 200,
    //     ridecompletedate: "2026-03-12T17:56:46.935345",
    //     paymentstatus: 4,
    //     paymentmethod: "5",
    //     razorpayrefundid: null,
    //     refundinitiatedon: null,
    //     refundtype: null,
    //     bookingamount: 1100,
    //     auditlist: [
    //       {
    //         auditid: 983,
    //         categoryid: 2,
    //         description: "Refund Initiated",
    //         userid: 40,
    //         bookingid: 316,
    //         planid: 124,
    //         refundid: 1,
    //         planname: "Weekly",
    //         enteredon: "2026-04-30T14:49:41.329288",
    //       },
    //     ],
    //   },
    // ];

    navigate(
      `/refund-management/edit-refund/${encodeURIComponent(
        String(row.refundId),
      )}`,
      {
        state: {
          /** Single refund record (same shape as API); replace static `data[0]` with `row.rawItem` when removing mock */
          data: row.rawItem,
          page: pagination.page,
        },
      },
    );
  };

  const openDeleteRefundDialog = (row: RefundRow) => {
    setDeleteRefundReasonCode("");
    setDeleteRefundRemark("");
    setRefundPendingDelete(row);
  };

  const cancelDeleteRefund = () => {
    if (deleteRefundLoading) return;
    setRefundPendingDelete(null);
    setDeleteRefundReasonCode("");
    setDeleteRefundRemark("");
  };

  const confirmDeleteRefund = async () => {
    if (!refundPendingDelete) return;
    const resolved = resolveRefundDeleteReasonPayload(
      deleteRefundReasonSelectOptions,
      deleteRefundReasonOtherCode,
      deleteRefundReasonCode,
      deleteRefundRemark,
    );
    if (!resolved.ok) {
      showNotification(resolved.error, "error");
      return;
    }
    const { deletereason } = resolved;
    const refundId = refundPendingDelete.refundId;
    if (String(refundId).trim() === "") {
      showNotification("Invalid refund id.", "error");
      return;
    }
    setDeleteRefundLoading(true);
    try {
      const res = (await apiService.deleteRefund({
        refundid: refundId,
        deletereason: deletereason,
        deleterefundreasonid: Number(deleteRefundReasonCode),
      })) as {
        isSuccess?: boolean;
        message?: string;
      };
      if (res && typeof res === "object" && res.isSuccess === false) {
        throw new Error(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message
            : "Delete refund failed.",
        );
      }
      const msg =
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : "Refund deleted.";
      showNotification(msg, "success");
      setRefundPendingDelete(null);
      setDeleteRefundReasonCode("");
      setDeleteRefundRemark("");
      await fetchRefunds();
      if (listTab === "conflicts") {
        await fetchConflictRefunds();
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to delete refund.";
      showNotification(message, "error");
    } finally {
      setDeleteRefundLoading(false);
    }
  };

  const columns: SortableColumn<RefundRow>[] = [
    { id: "bookingMasterId", label: "Booking ID" },
    {
      id: "isAddedByWeb",
      label: "Source",
      align: "center",
      render: (row: RefundRow) => {
        const manual = row.isAddedByWeb;
        return (
          <Box display="flex" justifyContent="center">
            <Chip
              label={getRefundSourceLabel(manual)}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: manual
                  ? "rgba(25, 118, 210, 0.15)"
                  : "rgba(255, 193, 7, 0.35)",
                color: manual
                  ? "rgba(25, 118, 210, 1)"
                  : "rgba(184, 134, 11, 1)",
              }}
            />
          </Box>
        );
      },
    },
    { id: "name", label: "User name" },
    { id: "phoneNumber", label: "Mobile no." },
    { id: "hubName", label: "Hub" },
    {
      id: "vehicle",
      label: "Vehicle",
      render: (row: RefundRow) => row.vehicle || "—",
    },
    {
      id: "rideStartDate",
      label: "Ride start date",
      render: (row: RefundRow) =>
        formatRefundRideStartDate(row.rawItem) || row.rideStartDate || "—",
    },
    {
      id: "rideCompleteDate",
      label: "Ride completion date",
      render: (row: RefundRow) =>
        formatRefundRideEndDate(row.rawItem) || row.rideCompleteDate || "—",
    },
    {
      id: "duration",
      label: "Duration",
      render: (row: RefundRow) => row.duration || "—",
    },
    {
      id: "bookingAmount",
      label: "Amount",
      align: "center",
      render: (row: RefundRow) => {
        const amount =
          row.paymentStatus === REFUND_STATUS_CODE.PROCESSED
            ? row.refundableAmount
            : row.proposedRefundAmount;
        return `₹${amount}`;
      },
    },
    {
      id: "refundStatus",
      label: "Refund status",
      align: "center",
      render: (row: RefundRow) => {
        const code = String(row.refundStatus ?? "").trim();
        if (!code) return "—";
        const opt = refundStatusOptions.find(
          (item) => String(item.codevalue ?? "") === code,
        );
        const label = opt?.codedisplayname ? String(opt.codedisplayname) : code;
        return (
          <Box display="flex" justifyContent="center">
            <RefundStatusChip label={label} code={code} />
          </Box>
        );
      },
    },
    {
      id: "refundId",
      label: "Action",
      align: "center",
      sortable: false,
      render: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          {canEditOrDeleteRefund ? (
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="text"
                size="small"
                sx={{ minWidth: 36, p: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  openEditDialog(row);
                }}
                aria-label="Edit"
              >
                <img src={ActionIcon} alt="" width={20} height={20} />
              </Button>
              {row.paymentStatus === 1 ? (
                <Button
                  variant="text"
                  size="small"
                  sx={{ minWidth: 36, p: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteRefundDialog(row);
                  }}
                  aria-label="Delete"
                >
                  <img src={DeleteIcon} alt="" width={20} height={20} />
                </Button>
              ) : (
                <Box
                  aria-hidden
                  sx={{ width: 36, minWidth: 36, height: 36, flexShrink: 0 }}
                />
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const renderRefundTableContent = () => {
    if (tableLoading) {
      return <CustomLoading message="Fetching refund data..." />;
    }
    if (listTab === "conflicts") {
      return (
        <RefundConflictBookingTable
          groups={displayConflictGroups}
          columns={columns}
          expandedBookingIds={expandedConflictBookingIds}
          onToggleExpand={toggleConflictBookingExpand}
          sortBy={conflictSortBy}
          sortOrder={conflictSortOrder}
          onRequestSort={handleConflictRequestSort}
          emptyMessage="No conflict refunds found for the current filters"
        />
      );
    }
    return (
      <SortableTable
        data={rows}
        columns={columns}
        emptyMessage="No refunds found"
        getRowKey={(row) => String(row.refundId)}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container fluid className="px-0">
        <Paper className="p-3 mb-3 border-0 shadow-none rounded-0">
          <Row className="g-3 align-items-end">
            {isSuperAdmin && (
              <Col xs={12} md={3}>
                <SearchableSelectInput
                  field="hub"
                  formData={{ hub }}
                  onChange={(_, value) => {
                    setHub(value);
                    pagination.goToPage(0);
                  }}
                  options={hubNameOptions}
                  label="Hub"
                  placeholder="Search and select hub"
                  disabled={hubsLoading}
                />
              </Col>
            )}

            <Col xs={12} md={3}>
              <SearchableSelectInput
                field="refundStatus"
                label="Refund status"
                placeholder="Select refund status"
                formData={{ refundStatus: refundStatus || "" }}
                onChange={(_, value) => {
                  setRefundStatus(value);
                  pagination.goToPage(0);
                }}
                options={refundStatusSelectOptions}
              />
            </Col>
            <Col xs={12} md={3}>
              <DatePicker
                label="Ride start"
                value={rideDateRange[0]}
                onChange={(v) => {
                  setRideDateRange([v, rideDateRange[1]]);
                  pagination.goToPage(0);
                }}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => {
                      setRideDateRange([null, rideDateRange[1]]);
                      pagination.goToPage(0);
                    },
                  },
                  textField: { size: "small", fullWidth: true },
                }}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={12} md={3}>
              <DatePicker
                label="Ride completion"
                value={rideDateRange[1]}
                onChange={(v) => {
                  setRideDateRange([rideDateRange[0], v]);
                  pagination.goToPage(0);
                }}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => {
                      setRideDateRange([rideDateRange[0], null]);
                      pagination.goToPage(0);
                    },
                  },
                  textField: { size: "small", fullWidth: true },
                }}
                format="DD/MM/YYYY"
              />
            </Col>
          </Row>
          <Row className="g-3 align-items-end mt-1">
            {(
              [
                {
                  field: "bookingId" as const,
                  label: "Booking ID",
                  placeholder: "Booking ID",
                },
                {
                  field: "userName" as const,
                  label: "User name",
                  placeholder: "Name",
                },
                {
                  field: "mobileNumber" as const,
                  label: "Mobile no.",
                  placeholder: "Mobile number",
                },
                {
                  field: "vehicleNum" as const,
                  label: "Vehicle no.",
                  placeholder: "Vehicle number",
                },
              ] as const
            ).map(({ field, label, placeholder }) => (
              <Col xs={12} sm={6} md={3} key={field}>
                <TextField
                  label={label}
                  placeholder={placeholder}
                  size="small"
                  fullWidth
                  value={searchFilters[field]}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      [field]: e.target.value,
                    }))
                  }
                  InputProps={{
                    endAdornment: searchFilters[field] ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label={`Clear ${label}`}
                          edge="end"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() =>
                            setSearchFilters((prev) => ({
                              ...prev,
                              [field]: "",
                            }))
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Col>
            ))}
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          <Box
            sx={{
              px: 2,
              pt: 1,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Tabs
              value={listTab}
              onChange={handleListTabChange}
              aria-label="Refund list tabs"
            >
              <Tab label="All refunds" value="all" />
              <Tab
                label={`Conflicting Refunds${conflictBookingGroups.length > 0 ? ` (${conflictBookingGroups.length})` : ""}`}
                value="conflicts"
              />
            </Tabs>
          </Box>
          {renderRefundTableContent()}
        </Paper>

        <Pagination
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          totalItems={paginationTotalItems}
          totalPages={pagination.totalPages}
          startItem={pagination.startItem}
          endItem={pagination.endItem}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showRowsPerPage
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onGoToNextPage={pagination.goToNextPage}
          onGoToPreviousPage={pagination.goToPreviousPage}
        />

        <DeleteConfirmDialog
          open={refundPendingDelete != null}
          message={
            refundPendingDelete
              ? `Delete refund for booking ${refundPendingDelete.bookingMasterId}? This cannot be undone.`
              : ""
          }
          requireDeleteReason
          deleteReasonLabel="Delete reason"
          {...(deleteRefundReasonSelectOptions.length > 0
            ? {
                deleteReasonOptions: deleteRefundReasonSelectOptions,
                selectedDeleteReasonCode: deleteRefundReasonCode,
                onDeleteReasonCodeChange: setDeleteRefundReasonCode,
                deleteRemark: deleteRefundRemark,
                onDeleteRemarkChange: setDeleteRefundRemark,
                deleteReasonOtherCodeValue: deleteRefundReasonOtherCode,
              }
            : {
                deleteReason: deleteRefundRemark,
                onDeleteReasonChange: setDeleteRefundRemark,
              })}
          loading={deleteRefundLoading}
          onCancel={cancelDeleteRefund}
          onConfirm={() => void confirmDeleteRefund()}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default RefundManagementPage;
